import { Component } from '@theme/component';

/**
 * Home reviews strip: the two arrow buttons scroll the strip by one view and carry aria-disabled
 * (never `disabled`) at the ends, so keyboard focus stays on them.
 *
 * @extends {Component<{ scroller: HTMLElement, prev: HTMLElement, next: HTMLElement }>}
 */
class ZerbayHomeReviews extends Component {
  requiredRefs = ['scroller', 'prev', 'next'];
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    const { scroller, prev, next } = this.refs;
    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    scroller.addEventListener('scroll', this.#update, { passive: true });
    window.addEventListener('resize', this.#update);
    requestAnimationFrame(this.#update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller, prev, next } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    scroller?.removeEventListener('scroll', this.#update);
    window.removeEventListener('resize', this.#update);
  }

  #onPrev = () => this.#go(this.refs.prev, -1);
  #onNext = () => this.#go(this.refs.next, 1);

  /** Scrolls one view toward the inline start (-1) or end (1). */
  #go(button, direction) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const { scroller } = this.refs;
    const flip = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;
    scroller.scrollBy({ left: direction * flip * scroller.clientWidth, behavior: this.#motion.matches ? 'auto' : 'smooth' });
  }

  #update = () => {
    const { scroller, prev, next } = this.refs;
    if (!scroller) return;
    const position = Math.abs(scroller.scrollLeft);
    const max = scroller.scrollWidth - scroller.clientWidth - 1;
    prev.setAttribute('aria-disabled', String(position <= 1));
    next.setAttribute('aria-disabled', String(position >= max));
  };
}

if (!customElements.get('zerbay-home-reviews')) customElements.define('zerbay-home-reviews', ZerbayHomeReviews);
